<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { useLongPressTooltip } from "@/composable/useLongPressTooltip";
import { useGetSaveReminderInfo } from "@/composable/useSaveReminder";
import { manualSave } from "@/stores/SaveDrawingState";
import Settings from "@/stores/Settings";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { computed } from "vue";

const { t } = useTranslation();
const { refHook } = useLongPressTooltip();
const { showBanner, dismiss } = useGetSaveReminderInfo();

// Sauvegarde automatique désactivée : ce badge est le seul filet de sécurité,
// on l'accentue (rouge) et on le dit explicitement.
const autoSaveOff = computed(() => !Settings.autoSaveOn);

const title = computed(() =>
  autoSaveOff.value
    ? t("canvas:save.reminderBanner.titleNoAutoSave")
    : t("canvas:save.reminderBanner.title"),
);

const message = computed(() =>
  autoSaveOff.value
    ? t("canvas:save.reminderBanner.messageNoAutoSave")
    : t("canvas:save.reminderBanner.message"),
);

function handleClick() {
  manualSave();
}

function handleDismiss(e: Event) {
  e.stopPropagation();
  dismiss();
}
</script>

<template>
  <span
    v-if="showBanner"
    ref="refHook"
    class="save-reminder-badge"
    :class="{ 'no-autosave': autoSaveOff }"
    :data-bs-content="message"
    :data-bs-title="title"
    @click="handleClick"
  >
    <PhosphorIcon name="warning" :size="14" weight="fill" />
    <span class="reminder-label">{{ title }}</span>
    <button class="reminder-dismiss" @click="handleDismiss" aria-label="Close">
      <PhosphorIcon name="x" :size="10" />
    </button>
  </span>
</template>

<style lang="scss" scoped>
.save-reminder-badge {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.5rem;
  border-radius: 1rem;
  background-color: var(--bs-warning);
  color: #000;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  animation: reminder-pulse 2s ease-in-out infinite;

  &:hover {
    filter: brightness(0.95);
  }

  &.no-autosave {
    background-color: var(--bs-danger);
    color: #fff;
    animation-name: reminder-pulse-danger;

    .reminder-dismiss {
      color: #fff;
    }
  }
}

.reminder-dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin-left: 0.1rem;
  color: #000;
  opacity: 0.6;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
}

@keyframes reminder-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--bs-warning) 60%, transparent);
  }
  50% {
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--bs-warning) 0%, transparent);
  }
}

@keyframes reminder-pulse-danger {
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--bs-danger) 60%, transparent);
  }
  50% {
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--bs-danger) 0%, transparent);
  }
}
</style>
